import { api } from "./api";

let _logoCache = null;
let _sigCache = null;
let _settingsCache = null;
let _settingsCacheAt = 0;

/** Loads /dr-dimy-brand.jpeg as a data URL (cached). Uses the brand image directly (not the SVG logo). */
export async function loadLogoDataUrl() {
  if (_logoCache) return _logoCache;
  try {
    const res = await fetch("/dr-dimy-brand.jpeg");
    if (!res.ok) return null;
    const blob = await res.blob();
    _logoCache = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    return _logoCache;
  } catch (e) {
    return null;
  }
}

/** Loads /dr-dimy-signature.png as a data URL (cached). Handwritten signature image. */
export async function loadSignatureDataUrl() {
  if (_sigCache) return _sigCache;
  try {
    const res = await fetch("/dr-dimy-signature.png");
    if (!res.ok) return null;
    const blob = await res.blob();
    _sigCache = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    return _sigCache;
  } catch (e) {
    return null;
  }
}

/** Fetch app settings (cached for 60s). */
export async function getSettingsCached() {
  if (_settingsCache && Date.now() - _settingsCacheAt < 60_000) return _settingsCache;
  try {
    const { data } = await api.get("/settings");
    _settingsCache = data; _settingsCacheAt = Date.now();
    return data;
  } catch (e) { return _settingsCache || {}; }
}

/**
 * Draws an ECG (electrocardiogram) polyline from x1 to x2 at baseline y.
 * Produces a realistic P-QRS-T waveform pattern.
 */
function drawEcgLine(doc, x1, x2, y, opts = {}) {
  const {
    color = [176, 141, 87],
    lineWidth = 0.6,
    amplitude = 6,
    beats = 3,
  } = opts;
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setLineWidth(lineWidth);
  const totalW = x2 - x1;
  const beatW = totalW / beats;
  // Each beat is drawn as a sequence of line segments (relative offsets)
  // Pattern (dx, dyFromBaseline) — baseline flat, tiny P, big R spike, small T
  const pattern = [
    [0.00, 0],      // start baseline
    [0.18, 0],      // flat
    [0.22, -0.25],  // P wave up
    [0.26, 0],      // back
    [0.32, 0],      // flat PR
    [0.34, 0.35],   // Q dip
    [0.37, -1.0],   // R spike up (dominant)
    [0.40, 0.55],   // S dip
    [0.43, 0],      // ST segment
    [0.55, 0],      // ST flat
    [0.62, -0.35],  // T wave up
    [0.68, 0],      // back
    [1.00, 0],      // baseline to end
  ];
  for (let b = 0; b < beats; b++) {
    const bx = x1 + b * beatW;
    for (let i = 0; i < pattern.length - 1; i++) {
      const [dx1, dy1] = pattern[i];
      const [dx2, dy2] = pattern[i + 1];
      doc.line(
        bx + dx1 * beatW,
        y + dy1 * amplitude,
        bx + dx2 * beatW,
        y + dy2 * amplitude
      );
    }
  }
}

/**
 * Draws a professional letterhead-style header on the PDF.
 * Vector-only: shield emblem with ECG waveform + monogram, elegant typography, ECG accent detail.
 * Returns the y position where body content should start.
 */
export async function drawProHeader(doc, opts = {}) {
  const {
    docTitle = "Documento médico",
    docSubtitle = "",
    settings = {},
    doctor = {},
    color = [15, 118, 110], // teal-700
  } = opts;

  const W = doc.internal.pageSize.getWidth();
  const margin = 48;
  const headerH = 156;
  const accent = [176, 141, 87]; // muted gold accent
  const deep = [8, 74, 68];      // deeper teal for shadow/edge
  const rightBlockW = 200;                 // reserved width for the right title block
  const leftBlockRight = W - margin - rightBlockW - 14; // right edge of left column

  // Top accent bar: solid teal + gold hairline + subtle ECG echo
  doc.setFillColor(color[0], color[1], color[2]);
  doc.rect(0, 0, W, 5, "F");
  doc.setFillColor(accent[0], accent[1], accent[2]);
  doc.rect(0, 5, W, 0.8, "F");
  // ECG echo across the top (very subtle, gold at low opacity via light color)
  drawEcgLine(doc, margin, W - margin, 12, {
    color: [212, 184, 130], // soft gold
    lineWidth: 0.35,
    amplitude: 3.2,
    beats: 8,
  });

  // ---- Emblem: CIRCLE with ECG waveform + medical cross + "DL" monogram ----
  const emblemCX = margin + 28;
  const emblemCY = 66;
  const emblemR = 26;

  // Outer deep-teal disc
  doc.setFillColor(deep[0], deep[1], deep[2]);
  doc.setDrawColor(accent[0], accent[1], accent[2]);
  doc.setLineWidth(1.1);
  doc.circle(emblemCX, emblemCY, emblemR, "FD");

  // Inner gold hairline ring (offset inward for elegance)
  doc.setDrawColor(accent[0], accent[1], accent[2]);
  doc.setLineWidth(0.4);
  doc.circle(emblemCX, emblemCY, emblemR - 2.6, "S");

  // ECG waveform crossing the circle (bright teal accent on deep bg)
  drawEcgLine(doc, emblemCX - emblemR + 5, emblemCX + emblemR - 5, emblemCY - 2, {
    color: [125, 211, 192], // teal-300 (bright ECG green-teal)
    lineWidth: 0.9,
    amplitude: 6.5,
    beats: 1,
  });

  // Tiny medical cross above ECG — subtle gold
  doc.setDrawColor(accent[0], accent[1], accent[2]);
  doc.setLineWidth(0.7);
  const crossY = emblemCY - emblemR + 7;
  doc.line(emblemCX - 2.6, crossY, emblemCX + 2.6, crossY);
  doc.line(emblemCX, crossY - 2.6, emblemCX, crossY + 2.6);

  // Monogram "DL" in serif — placed just below ECG for balance
  doc.setTextColor(255, 255, 255);
  doc.setFont("times", "bold");
  doc.setFontSize(15);
  doc.text("DL", emblemCX, emblemCY + 13, { align: "center" });

  // ---- Doctor name + credentials (right of emblem) ----
  const textX = margin + 68;
  const leftBlockMaxW = leftBlockRight - textX;

  // Tiny "eyebrow" label above the name — very formal
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(accent[0], accent[1], accent[2]);
  doc.text("CONSULTA MÉDICA PRIVADA", textX, 40);

  doc.setFont("times", "bold");
  doc.setFontSize(20);
  doc.setTextColor(15, 23, 42);
  // Truncate name if it overflows the left column
  let nameStr = doctor.name || "Dr. Dimy Laguerre";
  while (doc.getTextWidth(nameStr) > leftBlockMaxW && nameStr.length > 10) {
    nameStr = nameStr.slice(0, -2);
  }
  doc.text(nameStr, textX, 58);

  // Specialty + colegiado line (no letter-spacing hack; wrap if needed)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  const specialty = (doctor.specialty || "Especialista en MUE").toUpperCase();
  const lic = doctor.license_number || "41/4119650";
  const college = (doctor.college || "").toUpperCase();
  const specLine = college
    ? `${specialty}  ·  COLEGIADO ${lic}  ·  ${college}`
    : `${specialty}  ·  COLEGIADO ${lic}`;
  const specLines = doc.splitTextToSize(specLine, leftBlockMaxW);
  doc.text(specLines, textX, 76);
  const specHeight = specLines.length * 10;

  // Address / email / phone row (address only if set)
  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  const contactY = 76 + specHeight + 6;
  const parts = [
    settings.clinic_address || "",
    settings.clinic_email || "djimlag25@gmail.com",
    settings.clinic_phone || "+34 742 08 65 96",
  ].filter((x) => x && String(x).trim());
  const contactLine = parts.join("   |   ");
  const contactLines = doc.splitTextToSize(contactLine, leftBlockMaxW);
  doc.text(contactLines, textX, contactY);

  // ---- Right side: document title block ----
  const rightX = W - margin;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(accent[0], accent[1], accent[2]);
  doc.text("DOCUMENTO CLÍNICO", rightX, 40, { align: "right" });

  doc.setFontSize(15);
  doc.setTextColor(color[0], color[1], color[2]);
  doc.setFont("times", "bold");
  // Wrap title within the right block width if needed
  const titleLines = doc.splitTextToSize(docTitle, rightBlockW);
  let titleY = 58;
  titleLines.forEach((ln, i) => {
    doc.text(ln, rightX, titleY + i * 17, { align: "right" });
  });
  // Thin gold underline for the title
  const lastTitleY = titleY + (titleLines.length - 1) * 17;
  doc.setDrawColor(accent[0], accent[1], accent[2]);
  doc.setLineWidth(0.5);
  const uW = Math.min(rightBlockW, 120);
  doc.line(rightX - uW, lastTitleY + 4, rightX, lastTitleY + 4);

  if (docSubtitle) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    const subY = lastTitleY + 16;
    const subLines = doc.splitTextToSize(docSubtitle, rightBlockW);
    subLines.forEach((ln, i) => {
      doc.text(ln, rightX, subY + i * 10, { align: "right" });
    });
  }

  // ---- Elegant separator: teal rule + centered ECG mini-motif + gold rule ----
  const ruleY = headerH - 16;
  doc.setDrawColor(color[0], color[1], color[2]);
  doc.setLineWidth(0.9);
  // Split the top rule around a small centered ECG detail
  const ecgW = 70;
  const ecgCx = W / 2;
  doc.line(margin, ruleY, ecgCx - ecgW / 2 - 4, ruleY);
  doc.line(ecgCx + ecgW / 2 + 4, ruleY, W - margin, ruleY);
  // Small ECG motif at the center of the top rule
  drawEcgLine(doc, ecgCx - ecgW / 2, ecgCx + ecgW / 2, ruleY, {
    color: color,
    lineWidth: 0.75,
    amplitude: 4.5,
    beats: 1,
  });
  // Gold hairline below
  doc.setDrawColor(accent[0], accent[1], accent[2]);
  doc.setLineWidth(0.35);
  doc.line(margin, ruleY + 3.2, W - margin, ruleY + 3.2);

  // Reset
  doc.setFont("helvetica", "normal");
  doc.setTextColor(15, 23, 42);

  return headerH;
}

/** Draws the bottom-right QR plus signature placeholder. */
export async function drawProFooter(doc, opts = {}) {
  const {
    settings = {},
    doctor = {},
    qrUrl = "",
    qrLabel = "Verificar documento",
    sigLabel = "Firma del profesional",
    signature = null,
    patientSignature = null, // if set (any truthy), draws patient sig on LEFT and doctor sig on RIGHT (aligned)
  } = opts;
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const margin = 48;

  // Signature layout: default = doctor on LEFT, QR on RIGHT.
  // When patientSignature is truthy = patient on LEFT, doctor on RIGHT, QR shifted up-right.
  const twoSig = !!patientSignature;
  const docSigX = twoSig ? W - margin - 260 : margin;
  const docSigLineTo = docSigX + 220;

  if (twoSig) {
    // ---- Patient signature block (LEFT) ----
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.5);
    doc.line(margin, H - 130, margin + 220, H - 130);
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(patientSignature.label || "Firma del Paciente / Consentimiento Informado", margin, H - 116);
    if (patientSignature.name) {
      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(patientSignature.name, margin, H - 102);
    }
    if (patientSignature.nif) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(`DNI/NIE: ${patientSignature.nif}`, margin, H - 90);
    }
    if (patientSignature.consent) {
      doc.setTextColor(15, 118, 110);
      doc.setFont("helvetica", "italic");
      doc.setFontSize(8);
      doc.text("Consentimiento informado otorgado electrónicamente", margin, H - 138);
      doc.setFont("helvetica", "normal");
    }
  }

  // ---- Doctor signature block ----
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.5);
  doc.line(docSigX, H - 130, docSigLineTo, H - 130);
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  doc.text(sigLabel, docSigX, H - 116);
  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.text(doctor.name || "Dr. Laguerre Dimy", docSigX, H - 102);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  if (doctor.specialty) {
    doc.text(String(doctor.specialty).length > 46 ? String(doctor.specialty).slice(0, 44) + "…" : String(doctor.specialty), docSigX, H - 90);
  }
  if (doctor.license_number) doc.text(`Nº Colegiado: ${doctor.license_number}`, docSigX, H - 78);

  // Electronic signature block (positioned to avoid overlap)
  if (signature && signature.hash) {
    const sigX = twoSig ? margin + 240 : margin + 236; // center in twoSig, right-of-left in default
    doc.setFontSize(8);
    doc.setTextColor(15, 118, 110);
    doc.setFont("helvetica", "bold");
    doc.text("FIRMA ELECTRÓNICA SIMPLE (eIDAS)", sigX, H - 158);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(7.5);
    const signedAt = signature.signed_at ? new Date(signature.signed_at).toLocaleString("es-ES", { timeZone: "Europe/Madrid" }) : "-";
    doc.text(`Sellado: ${signedAt}`, sigX, H - 146);
    doc.text(`Algoritmo: ${signature.algo || "HMAC-SHA256"}`, sigX, H - 136);
    doc.setFont("courier", "bold");
    doc.setFontSize(8);
    doc.setTextColor(15, 23, 42);
    doc.text(`HASH ${signature.short || signature.hash.slice(0, 12).toUpperCase()}`, sigX, H - 124);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    doc.setTextColor(100, 116, 139);
    doc.text("Verificable vía QR · Cualquier cambio invalida el hash", sigX, H - 114);
  }

  // QR — in twoSig mode, place it in the CENTER (small) above the space between signatures
  if (qrUrl) {
    try {
      const QRCode = (await import("qrcode")).default;
      const dataUrl = await QRCode.toDataURL(qrUrl, { margin: 0, width: 220 });
      if (twoSig) {
        const qSize = 66;
        const qX = W / 2 - qSize / 2;
        doc.addImage(dataUrl, "PNG", qX, H - 210, qSize, qSize);
        doc.setFontSize(7.5);
        doc.setTextColor(100, 116, 139);
        doc.text(qrLabel, W / 2, H - 138, { align: "center" });
      } else {
        doc.addImage(dataUrl, "PNG", W - margin - 90, H - 200, 90, 90);
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(qrLabel, W - margin - 45, H - 100, { align: "center" });
      }
    } catch (e) { /* ignore */ }
  }

  // Footer line
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, H - 60, W - margin, H - 60);
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  const clinic = settings.clinic_name || "Dr. Dimy Laguerre";
  doc.text(
    `${clinic}  ·  ${settings.clinic_email || "djimlag25@gmail.com"}  ·  ${settings.clinic_phone || "+34 742 08 65 96"}`,
    W / 2, H - 44, { align: "center" }
  );
  doc.text(
    "Documento generado electrónicamente · Verifique mediante el código QR.",
    W / 2, H - 32, { align: "center" }
  );
}
